import { calculateQuote } from '@estac1/shared';
import { toast } from '@estac1/ui';
import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { AuditEntry, PricingSettings, TierForm } from './types.js';
import { SAMPLE_VACANCIES, validate } from './utils.js';

interface UsePricingFormInput {
  settingsData: PricingSettings | undefined;
  auditEntries: AuditEntry[] | undefined;
  dirtyRef: MutableRefObject<boolean>;
  save: { mutate: (input: PricingSettings) => void };
}

export const usePricingForm = ({
  settingsData,
  auditEntries,
  dirtyRef,
  save,
}: UsePricingFormInput) => {
  const [tiers, setTiersRaw] = useState<TierForm[]>([]);
  const [minimumCents, setMinimumCentsRaw] = useState(0);
  const [maxVacancies, setMaxVacanciesRaw] = useState(0);
  const [trialDays, setTrialDaysRaw] = useState(0);
  const [previewVacancies, setPreviewVacancies] = useState(30);
  const [expandedHistory, setExpandedHistory] = useState<Set<string>>(new Set());

  const markDirty =
    <T>(setter: Dispatch<SetStateAction<T>>): Dispatch<SetStateAction<T>> =>
    (value) => {
      dirtyRef.current = true;
      setter(value);
    };
  const setTiers = markDirty(setTiersRaw);
  const setMinimumCents = markDirty(setMinimumCentsRaw);
  const setMaxVacancies = markDirty(setMaxVacanciesRaw);
  const setTrialDays = markDirty(setTrialDaysRaw);

  const toggleHistory = (id: string) => {
    setExpandedHistory((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (settingsData && !dirtyRef.current) {
      setTiersRaw(
        settingsData.tiers.map((t, i) => ({
          ...t,
          _id: `seed-${i}`,
        })),
      );
      setMinimumCentsRaw(settingsData.minimumCents);
      setMaxVacanciesRaw(settingsData.maxVacancies);
      setTrialDaysRaw(settingsData.trialDays);
    }
  }, [settingsData, dirtyRef]);

  const validationError = useMemo(
    () => validate({ tiers, minimumCents, maxVacancies, trialDays }),
    [tiers, minimumCents, maxVacancies, trialDays],
  );

  const previewSamples = useMemo(() => {
    const list: number[] = [...SAMPLE_VACANCIES];
    if (!list.includes(previewVacancies)) {
      list.push(previewVacancies);
    }
    return list
      .sort((a, b) => a - b)
      .map((v) => ({
        vacancies: v,
        quote: calculateQuote({
          vacancies: v,
          tiers,
          minimumCents,
          maxVacancies,
        }),
      }));
  }, [tiers, minimumCents, maxVacancies, previewVacancies]);

  const pricingHistory = useMemo(() => {
    if (!auditEntries) return [];
    return auditEntries
      .filter((e) => e.entity === 'platform_settings' && e.entityId === 'pricing')
      .slice(0, 10);
  }, [auditEntries]);

  const updateTier = (idx: number, patch: Partial<PricingSettings['tiers'][number]>) => {
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const addTier = () => {
    const last = tiers[tiers.length - 1];
    const nextId = `new-${tiers.length}-${Math.max(0, ...tiers.map((t) => t.maxVacancies))}`;
    setTiers([
      ...tiers,
      {
        minVacancies: last ? last.maxVacancies + 1 : 1,
        maxVacancies: last ? last.maxVacancies + 50 : 50,
        monthlyCents: last ? last.monthlyCents + 2000 : 3990,
        _id: nextId,
      },
    ]);
  };

  const removeTier = (idx: number) => {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    if (validationError) {
      toast.error(validationError);
      return;
    }
    save.mutate({ tiers, minimumCents, maxVacancies, trialDays });
  };

  return {
    tiers,
    minimumCents,
    maxVacancies,
    trialDays,
    previewVacancies,
    expandedHistory,
    validationError,
    previewSamples,
    pricingHistory,
    setMinimumCents,
    setMaxVacancies,
    setTrialDays,
    setPreviewVacancies,
    toggleHistory,
    updateTier,
    addTier,
    removeTier,
    handleSave,
  };
};
